/**
 * GladiaWebSocketClient
 * ---------------------
 * Client WebSocket pour le streaming audio temps réel vers Gladia.
 *
 * Protocole Gladia Streaming v2 :
 *   1. POST /api/v1/transcription/live → le serveur ouvre la session Gladia
 *      avec sa clé et ne renvoie que { id, url } (v1.10.0 : plus aucune clé
 *      dans la page)
 *   2. WS connect sur l'URL de session
 *   3. Envoyer des chunks audio binaires (wav/pcm Int16 LE)
 *   4. Recevoir des messages JSON { type: "transcript", ... }
 *   5. POST /api/v1/transcription/live/:id/stop pour clore la session
 *
 * IMPORTANT: Gladia Live v2 n'accepte QUE wav/pcm, wav/alaw, wav/ulaw.
 *            webm/opus n'est PAS supporté.
 *
 * Doc : https://docs.gladia.io/api-reference/v2/live
 */

export interface GladiaConfig {
  /**
   * Langue source. "automatic" pour détection auto.
   * Default: "automatic"
   */
  language?: string;
  /**
   * Activer la traduction en temps réel.
   */
  translation?: {
    enabled: boolean;
    targetLanguage: string;
  };
  /**
   * Sample rate de l'audio envoyé.
   * Doit correspondre au sampleRate de AudioYMixer.
   * Default: 16000
   */
  sampleRate?: number;
}

export interface GladiaTranscript {
  type: 'transcript' | 'translation' | 'processing' | 'error';
  text?: string;
  confidence?: number;
  isFinal: boolean;
  language?: string;
  words?: Array<{ word: string; start: number; end: number; confidence: number }>;
}

export type TranscriptCallback = (transcript: GladiaTranscript) => void;
export type ErrorCallback = (error: Error) => void;

import { createLogger } from "./logger";
import { apiFetch } from "../services/api";
const slog = createLogger("gladia-ws");

export class GladiaWebSocketClient {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private readonly config: Required<Omit<GladiaConfig, 'translation'>> & Pick<GladiaConfig, 'translation'>;

  constructor(config: GladiaConfig) {
    this.config = {
      language: 'automatic',
      sampleRate: 16000,
      ...config,
    };
  }

  // ---------------------------------------------------------------------------
  // Démarrer la connexion Gladia (session + WS)
  // L'appelant envoie ensuite les chunks PCM via sendPCM().
  // ---------------------------------------------------------------------------
  async start(
    onTranscript: TranscriptCallback,
    onError?: ErrorCallback,
  ): Promise<void> {
    // 1. Créer la session Gladia
    const sessionUrl = await this._createSession();

    // 2. Ouvrir le WebSocket
    await this._connectWebSocket(sessionUrl, onTranscript, onError);
  }

  // ---------------------------------------------------------------------------
  // Envoyer un chunk PCM (Int16 LE) au WebSocket Gladia
  // ---------------------------------------------------------------------------
  sendPCM(pcmData: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN && pcmData.byteLength > 0) {
      this.ws.send(pcmData);
    }
  }

  // ---------------------------------------------------------------------------
  // Arrêter proprement
  // ---------------------------------------------------------------------------
  async stop(): Promise<void> {
    this.ws?.close();

    if (this.sessionId) {
      try {
        await apiFetch(`/transcription/live/${encodeURIComponent(this.sessionId)}/stop`, {
          method: 'POST',
        });
      } catch (e) {
        slog.warn("Erreur lors du stop session", { error: String(e) });
      }
    }

    this.ws = null;
    this.sessionId = null;
  }

  // ---------------------------------------------------------------------------
  // Interne : créer session Gladia
  // ---------------------------------------------------------------------------
  private async _createSession(): Promise<string> {
    const body: Record<string, unknown> = {
      encoding: 'wav/pcm',
      sample_rate: this.config.sampleRate,
      bit_depth: 16,
      channels: 1,
      language_config: {
        languages: this.config.language === 'automatic' ? [] : [this.config.language],
        code_switching: this.config.language === 'automatic',
      },
    };

    if (this.config.translation?.enabled) {
      body.translation_config = {
        target_languages: [this.config.translation.targetLanguage],
      };
    }

    const res = await apiFetch('/transcription/live', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.text();
      throw new Error(`[Gladia] Création session échouée (${res.status}): ${err}`);
    }

    const data = await res.json();
    this.sessionId = data.id;
    return data.url; // URL WebSocket de la session
  }

  // ---------------------------------------------------------------------------
  // Interne : connexion WebSocket
  // ---------------------------------------------------------------------------
  private _connectWebSocket(
    url: string,
    onTranscript: TranscriptCallback,
    onError?: ErrorCallback,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(url);
      this.ws.binaryType = 'arraybuffer';

      this.ws.onopen = () => {
        slog.info("WebSocket connecté");
        resolve();
      };

      this.ws.onerror = () => {
        const err = new Error('[Gladia] WebSocket error');
        onError?.(err);
        reject(err);
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          // Log ALL Gladia messages to understand the exact format
          slog.info("WS msg", { type: msg.type, payload: JSON.stringify(msg).slice(0, 300) });

          if (msg.type === 'transcript' || msg.type === 'translation') {
            // Try all known field paths for the transcript text
            const data = msg.data ?? msg;
            const text = data.transcription
              ?? data.translation
              ?? data.utterance?.text
              ?? data.text
              ?? msg.transcription
              ?? msg.text
              ?? '';
            onTranscript({
              type: msg.type,
              text,
              confidence: data.confidence,
              isFinal: data.is_final ?? msg.is_final ?? false,
              language: data.language ?? msg.language,
              words: data.words,
            });
          }
        } catch {
          // message non-JSON ignoré
        }
      };

      this.ws.onclose = () => {
        slog.info("WebSocket fermé");
      };
    });
  }
}
